import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { getComicsByCategory, comics } from '@/mocks/comics';
import { siteConfig } from '@/mocks/siteConfig';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'rexue';

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const categoryComics = useMemo(() => getComicsByCategory(activeSlug), [activeSlug]);

  const pageTitle = activeCategory
    ? `${activeCategory.name}漫画 - 免费阅读下拉式漫画 - 89890漫画网`
    : '漫画分类 - 89890漫画网';
  const pageDesc = activeCategory
    ? `89890漫画网${activeCategory.name}漫画专区，${activeCategory.description}。免费阅读下拉式漫画，滑动阅读便捷省力，让追漫成为一种享受。`
    : siteConfig.description;
  const pageKeywords = activeCategory
    ? `${activeCategory.name}漫画,免费阅读,下拉式漫画,89890漫画网,在线漫画`
    : siteConfig.keywords;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `89890漫画网的${activeCategory?.name || ''}漫画免费吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `89890漫画网所有${activeCategory?.name || ''}漫画完全免费，无需注册即可在线畅读。我们提供流畅的下拉式阅读体验，让您享受便捷省力的追漫之旅。`,
        },
      },
      {
        '@type': 'Question',
        name: `如何在89890漫画网阅读${activeCategory?.name || ''}漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '只需点击您喜欢的漫画封面，进入阅读页面后向下滑动即可连续阅读。我们的下拉式漫画模式让您无需翻页，滑动流畅，阅读体验极佳。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteConfig.url}/category/${activeSlug}`}
        schemaObjects={[faqSchema]}
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类' },
              ...(activeCategory ? [{ label: activeCategory.name }] : []),
            ]}
          />

          <section className="py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  {activeCategory ? (
                    <><strong>{activeCategory.name}</strong>漫画</>
                  ) : (
                    '漫画分类'
                  )}
                </h1>
                {activeCategory && (
                  <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                    {activeCategory.description}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap justify-center gap-2 mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 border border-background-200'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {categoryComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类下暂无漫画，去看看其他分类吧！</p>
                  <Link
                    to="/category/rexue"
                    className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-fire-line"></i>
                    看看热血漫画
                  </Link>
                </div>
              )}
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    猜你喜欢
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">推荐更多精彩内容</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {comics.filter(c => c.category !== activeSlug).slice(0, 4).map((comic) => (
                  <ComicCard key={comic.id} comic={comic} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}